import { cn } from '@/lib/cn'
import type { ReactNode } from 'react'

export function Card({
  className,
  interactive = false,
  children,
}: {
  className?: string
  interactive?: boolean
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] border border-line/70 bg-surface shadow-card',
        interactive &&
          'transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-raised',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 border-b border-line px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-ink">{title}</h2>
        {subtitle ? (
          <p className="mt-0.5 text-micro text-ink-faint">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 gap-1.5">{actions}</div> : null}
    </div>
  )
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('px-4 py-3.5', className)}>{children}</div>
}

/** Card with no header, used as a bordered section inside a form. */
export function CardSection({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={className}>
      {title ? (
        <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4 pb-1">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-micro text-ink-faint">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cn('px-4 py-4', title && 'pt-3')}>{children}</div>
    </Card>
  )
}
